import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../partials/Header';
import Footer from '../../partials/Footer';
import PageIllustration from '../../partials/PageIllustration';
import { inspectCid, SAMPLE_CIDS } from '../../utils/ipfs';

function Row({ label, value, mono = true, warn }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-gray-700/60 last:border-0">
      <span className="text-gray-500 text-sm sm:w-44 shrink-0">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} text-sm break-all ${warn ? 'text-yellow-400' : 'text-gray-200'}`}>{value}</span>
    </div>
  );
}

function CopyValue({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-300 text-sm font-medium">{label}</span>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="text-xs px-2 py-1 rounded bg-gray-600 text-gray-300 hover:bg-gray-500 transition-colors"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="bg-gray-700 rounded-md px-4 py-3">
        <code className="text-purple-300 font-mono text-sm break-all">{value}</code>
      </div>
    </div>
  );
}

function IpfsCidInspector() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const inspect = (value) => {
    try {
      setResult(inspectCid(value ?? input));
      setError('');
    } catch (e) {
      setResult(null);
      setError(e.message);
    }
  };

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>IPFS CID Inspector &amp; Converter — CIDv0 ↔ CIDv1</title>
        <meta name="description" content="Inspect any IPFS CID: decode multibase, multicodec, and multihash, and convert between CIDv0 and CIDv1 — free, no signup, runs in your browser." />
        <meta name="keywords" content="cid inspector, ipfs cid, cidv0 to cidv1, cid converter, multihash decoder, multibase, ipfs tools, content identifier" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.ogtechnologies.co/tools/ipfs-cid-inspector/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ogtechnologies.co/tools/ipfs-cid-inspector/" />
        <meta property="og:title" content="IPFS CID Inspector &amp; Converter — CIDv0 ↔ CIDv1" />
        <meta property="og:description" content="Decode any IPFS CID — multibase, multicodec, multihash — and convert between CIDv0 and CIDv1. Free, in-browser." />
        <meta property="og:image" content="https://www.ogtechnologies.co/og-og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.ogtechnologies.co/tools/ipfs-cid-inspector/" />
        <meta name="twitter:title" content="IPFS CID Inspector &amp; Converter — CIDv0 ↔ CIDv1" />
        <meta name="twitter:description" content="Decode any IPFS CID — multibase, multicodec, multihash — and convert between CIDv0 and CIDv1. Free, in-browser." />
        <meta name="twitter:image" content="https://www.ogtechnologies.co/og-og-image.png" />
      </Helmet>

      <Header />

      <main className="grow">
        <div className="relative max-w-6xl mx-auto h-0 pointer-events-none" aria-hidden="true">
          <PageIllustration />
        </div>

        <section className="relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="pt-32 pb-12 md:pt-40 md:pb-20">
              <div className="max-w-3xl mx-auto text-center pb-12 md:pb-16">
                <h1 className="h1">IPFS CID Inspector &amp; Converter</h1>
                <p className="text-xl text-gray-400 mt-4">
                  Decode any IPFS Content Identifier into its multibase, version, multicodec, and multihash components —
                  and convert between CIDv0 and CIDv1. Everything runs in your browser.
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                  <div className="mb-4">
                    <label className="block text-gray-300 text-sm font-medium mb-1">CID to inspect (v0 or v1, any multibase)</label>
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); inspect(); } }}
                      rows={2}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-md border border-gray-600 focus:border-purple-500 focus:outline-none font-mono text-sm"
                      placeholder="QmbWqxBEKC3P8tqsKc98xmWNzrzDtRLMiMPL8wBuTGsMnR or bafybei…"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Try a sample:{' '}
                      <button type="button" className="text-purple-400 hover:text-purple-300" onClick={() => { setInput(SAMPLE_CIDS.v0); inspect(SAMPLE_CIDS.v0); }}>CIDv0</button>
                      {' · '}
                      <button type="button" className="text-purple-400 hover:text-purple-300" onClick={() => { setInput(SAMPLE_CIDS.v1); inspect(SAMPLE_CIDS.v1); }}>CIDv1</button>
                      {' · '}
                      <button type="button" className="text-purple-400 hover:text-purple-300" onClick={() => { setInput(SAMPLE_CIDS.raw); inspect(SAMPLE_CIDS.raw); }}>raw leaf</button>
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => inspect()}
                    disabled={!input.trim()}
                    className="w-full px-4 py-3 rounded-md bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
                  >
                    Inspect CID
                  </button>

                  {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

                  {result && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-200 mb-2">Decoded</h2>
                        <div className="bg-gray-900/60 rounded-md px-4 py-3 border border-gray-700">
                          <Row label="Human-readable" value={result.humanReadable} />
                          <Row label="CID version" value={`v${result.version}`} />
                          <Row label="Multibase" value={result.multibase} />
                          <Row label="Multicodec" value={`${result.codec} (0x${result.codecCode.toString(16)})`} />
                          <Row label="Hash function" value={`${result.hashFunction} (0x${result.hashCode.toString(16)})`} warn={result.weakHash} />
                          <Row label="Digest length" value={`${result.digestLength} bytes`} />
                          <Row label="Digest (hex)" value={result.digestHex} />
                          <Row label="Multihash (hex)" value={result.multihashHex} />
                          <Row label="CID byte length" value={`${result.byteLength} bytes`} />
                        </div>
                        {result.weakHash && (
                          <p className="text-xs text-yellow-400 mt-2">This CID uses a cryptographically weak hash function ({result.hashFunction}) — do not rely on it for integrity.</p>
                        )}
                        {result.isIpnsName && (
                          <p className="text-xs text-purple-400 mt-2">The codec is libp2p-key — this CID is conventionally used as an IPNS name.</p>
                        )}
                      </div>

                      <div>
                        <h2 className="text-lg font-semibold text-gray-200 mb-2">Conversions</h2>
                        <div className="space-y-3">
                          <CopyValue label="CIDv1 — base32 (canonical)" value={result.base32String} />
                          <CopyValue label="CIDv1 — base36 (subdomain-optimized)" value={result.base36String} />
                          {result.v0 && <CopyValue label="CIDv0 — base58btc" value={result.v0} />}
                          {!result.v0 && (
                            <p className="text-xs text-gray-500">
                              No CIDv0 form — v0 only exists for dag-pb CIDs with a 32-byte sha2-256 digest.
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Subdomain gateways ({'<cid>.ipfs.<gateway>'}) need a DNS-safe name (≤63 chars).{' '}
                            {result.subdomainCid
                              ? `Use ${result.subdomainCid === result.base32String ? 'the base32' : 'the base36'} form above.`
                              : 'This CID is too long for a DNS label — use a path-style gateway URL.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 text-xs text-gray-500">
                    <p><strong>CIDv0</strong> is the base58btc string of a sha2-256 multihash — always 46 chars starting with "Qm", with dag-pb implied.</p>
                    <p className="mt-1"><strong>CIDv1</strong> adds a self-describing header: multibase prefix, version, and multicodec before the multihash.</p>
                  </div>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  More IPFS tools:{' '}
                  <Link to="/tools/ipfs-gateway-checker/" className="text-purple-400 hover:text-purple-300">Gateway Checker</Link>
                  {' · '}
                  <Link to="/tools/ipfs-cid-generator/" className="text-purple-400 hover:text-purple-300">File → CID</Link>
                  {' · '}
                  <Link to="/tools/ipfs-url-converter/" className="text-purple-400 hover:text-purple-300">URL Converter</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default IpfsCidInspector;
